import { previewOrder, type CreateOrderRequest, type OrderPreview } from '@/api/orders';
import { getCurrentWalletAddress } from '@/common/services/request';

// At most the two directions for the current form, never a persisted price cache.
const MAX_AGE_MS = 10_000;
const SUBMIT_MARGIN_MS = 2_000;
type Entry = { preview: OrderPreview; expires: number };
const entries = new Map<string, Entry>();
const previewExpiries = new WeakMap<OrderPreview, number>();
let generation = 0;
let context = '';
const monotonicNow = () => performance.now();
const walletKey = () => (getCurrentWalletAddress() || '').toLowerCase().trim();
const keyFor = (body: CreateOrderRequest, wallet: string) =>
  JSON.stringify([wallet.toLowerCase().trim(), Object.keys(body).sort().map(key =>
    [key, body[key as keyof CreateOrderRequest]])]);

export function clearOrderPreviews(expectedGeneration?: number): void {
  if (expectedGeneration != null && expectedGeneration !== generation) return;
  generation++;
  entries.clear();
  context = '';
}

function storePreview(body: CreateOrderRequest, wallet: string, preview: OrderPreview, elapsed: number): void {
  const serverTime = Date.parse(preview.serverTime);
  if (!Number.isFinite(serverTime) || preview.funding?.canSubmit === false) return;
  const deadlines = [preview.acceptOrdersUntil, preview.submission?.expiresAt];
  if (preview.insuranceFundingQuoteId) {
    if (!preview.insuranceFundingQuoteExpiresAt) return;
    deadlines.push(preview.insuranceFundingQuoteExpiresAt);
  }
  if (preview.endsInsuranceSession || preview.insuranceConversionQuoteId) {
    // Older servers do not expose this TTL: fall back to an on-click preview.
    if (!preview.insuranceConversionQuoteId || !preview.insuranceConversionQuoteExpiresAt) return;
    deadlines.push(preview.insuranceConversionQuoteExpiresAt);
  }
  let ttl = MAX_AGE_MS;
  for (const deadline of deadlines) {
    if (!deadline) continue;
    const expiry = Date.parse(deadline);
    if (!Number.isFinite(expiry)) return;
    ttl = Math.min(ttl, expiry - serverTime - elapsed);
  }
  if (ttl <= SUBMIT_MARGIN_MS) return;
  const expires = Math.min(previewExpiries.get(preview) ?? Infinity, monotonicNow() + ttl);
  if (expires - monotonicNow() <= SUBMIT_MARGIN_MS) return;
  previewExpiries.set(preview, expires);
  if (entries.size >= 2) entries.delete(entries.keys().next().value!);
  entries.set(keyFor(body, wallet), { preview, expires });
}

/** Input/wallet changes invalidate both directions and any late in-flight result. */
export function setOrderPreviewContext(bodies: CreateOrderRequest[], wallet: string, revision = ''): number {
  const next = JSON.stringify([revision, bodies.map(body => keyFor(body, wallet))]);
  if (context === next) return generation;
  clearOrderPreviews();
  context = next;
  return generation;
}

/** Fire only for the visible, valid form. Failures never become user order failures. */
export async function prefetchOrderPreviews(bodies: CreateOrderRequest[], wallet: string, expectedGeneration = generation): Promise<void> {
  if (expectedGeneration !== generation || !wallet || walletKey() !== wallet.toLowerCase().trim()) return;
  const currentGeneration = generation;
  await Promise.all(bodies.slice(0, 2).map(async body => {
    const started = monotonicNow();
    try {
      const preview = await previewOrder(body, { expectedWallet: wallet, purpose: 'prefetch' });
      if (generation !== currentGeneration || walletKey() !== wallet.toLowerCase().trim()) return;
      storePreview(body, wallet, preview, monotonicNow() - started);
    } catch { /* A speculative preview must not debit, retry orders or surface a toast. */ }
  }));
}

/** Consumed only by the real click; final price and balances remain server-authoritative. */
export function takeOrderPreview(body: CreateOrderRequest, wallet: string): OrderPreview | null {
  if (walletKey() !== wallet.toLowerCase().trim()) return null;
  const key = keyFor(body, wallet);
  const entry = entries.get(key);
  entries.delete(key);
  if (!entry || entry.expires - monotonicNow() <= SUBMIT_MARGIN_MS) return null;
  return entry.preview;
}

/** First foreground conversion preview may be reused by the existing confirmation path. */
export function rememberConversionPreview(body: CreateOrderRequest, wallet: string, preview: OrderPreview, elapsed: number): void {
  if (walletKey() !== wallet.toLowerCase().trim()) return;
  storePreview(body, wallet, preview, elapsed);
}
